// src/pages/CustomerTable.js
import React, { useState, useEffect } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BASE_URL from '../../api';
import Swal from 'sweetalert2';
import Header from '../../layouts/Header';
import SideBar from '../../layouts/SideBar';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { IoIosFunnel } from 'react-icons/io';
import { FaArrowLeft, FaArrowRight, FaFileExcel, FaEye } from 'react-icons/fa';

const API_BASE_URL = `${BASE_URL}/api/master`;
const DEFAULT_CUSTOMER_ROLE_ID = '698b2f46a6d62379e83e28ca';

const CustomerTable = () => {
  const [customers, setCustomers] = useState([]);
  const [pincodes, setPincodes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // FETCH DATA WITH FALLBACK RETRY (Bypasses token failure on remote server for master lists)
  const fetchCustomers = async () => {
    try {
      let res = await fetch(`${API_BASE_URL}/customer`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/customer`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) throw new Error('Failed to fetch customers');
      const { data } = await res.json();
      setCustomers(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.message || 'Error fetching customers');
    }
  };

  const fetchPincodes = async () => {
    try {
      let res = await fetch(`${API_BASE_URL}/pincode`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/pincode`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) return;
      const { data } = await res.json();
      setPincodes(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchBranches = async () => {
    try {
      let res = await fetch(`${API_BASE_URL}/branch`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${API_BASE_URL}/branch`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) return;
      const { data } = await res.json();
      setBranches(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchRoles = async () => {
    try {
      let res = await fetch(`${BASE_URL}/api/role/roles`, { headers: getAuthHeaders() });
      if (!res.ok) {
        res = await fetch(`${BASE_URL}/api/role/roles`, { headers: { 'Content-Type': 'application/json' } });
      }
      if (!res.ok) throw new Error('Failed to fetch roles');
      const { data } = await res.json();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchPincodes(), fetchBranches(), fetchCustomers(), fetchRoles()]);
      setLoading(false);
    };
    if (localStorage.getItem('token')) load();
  }, []);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [filter]);

  // VALIDATION
  const validateForm = (data) => {
    const errors = {};
    if (!data.name?.trim()) errors.name = 'Customer Name is required';
    if (!data.email?.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(data.email)) errors.email = 'Invalid email format';

    if (!data.mobileNumber?.trim()) errors.mobileNumber = 'Mobile Number is required';
    else if (!/^\d{10}$/.test(data.mobileNumber)) errors.mobileNumber = 'Mobile must be 10 digits';

    if (data.gstNumber?.trim()) {
      const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
      if (!gstRegex.test(data.gstNumber.toUpperCase())) {
        errors.gstNumber = 'Invalid GST format';
      }
    }

    if (!data.contractSignedDate) errors.contractSignedDate = 'Contract Signed Date is required';
    if (!data.contractStartDate) errors.contractStartDate = 'Contract Start Date is required';
    if (!data.contractEndDate) errors.contractEndDate = 'Contract End Date is required';

    if (!data._id && !data.password?.trim()) errors.password = 'Password is required for new customer';

    if (!data.role) errors.role = 'Role is required';
    if (!data.pincode) errors.pincode = 'Pincode is required';
    if (!data.branch) errors.branch = 'Branch is required';
    return errors;
  };

  // CRUD OPERATIONS
  const createCustomer = async (data) => {
    const res = await fetch(`${API_BASE_URL}/customer`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to create customer');
    }
    return res.json();
  };

  const updateCustomer = async (id, data) => {
    const res = await fetch(`${API_BASE_URL}/customer/${id}`, {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to update customer');
    }
    return res.json();
  };

  const deleteCustomer = async (id) => {
    const res = await fetch(`${API_BASE_URL}/customer/${id}`, {
      method: 'DELETE',
      headers: getAuthHeaders(),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.message || 'Failed to delete customer');
    }
    return res.json();
  };

  const openCustomerModal = async (customer = null) => {
    const isEdit = !!customer;

    const { value: formData } = await Swal.fire({
      title: isEdit ? 'Edit Customer' : 'Add New Customer',
      html: `
      <div class="space-y-8 text-left text-sm p-2">
        <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h3 class="text-md font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span class="w-2 h-5 bg-blue-600 rounded-full"></span> Basic Information
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Name <span class="text-red-500">*</span></label>
              <input id="name" class="swal2-input !m-0 !w-full" value="${customer?.name || ''}" placeholder="Full Name">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Company Name <span class="text-red-500">*</span></label>
              <input id="companyName" class="swal2-input !m-0 !w-full" value="${customer?.companyName || ''}" placeholder="Company Ltd">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Email <span class="text-red-500">*</span></label>
              <input id="email" type="email" class="swal2-input !m-0 !w-full" value="${customer?.email || ''}" placeholder="email@example.com">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Mobile Number <span class="text-red-500">*</span></label>
              <input id="mobileNumber" class="swal2-input !m-0 !w-full" value="${customer?.mobileNumber || ''}" maxlength="10">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">GST Number</label>
              <input id="gstNumber" class="swal2-input !m-0 !w-full" value="${customer?.gstNumber || ''}" placeholder="22AAAAA0000A1Z5" style="text-transform: uppercase;">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">PAN Number</label>
              <input id="panNumber" class="swal2-input !m-0 !w-full" value="${customer?.panNumber || ''}" placeholder="ABCDE1234F" style="text-transform: uppercase;">
            </div>
            ${!isEdit ? `
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Password <span class="text-red-500">*</span></label>
              <input id="password" type="password" class="swal2-input !m-0 !w-full" placeholder="••••••••">
            </div>
            ` : ''}
          </div>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h3 class="text-md font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span class="w-2 h-5 bg-blue-600 rounded-full"></span> Contract Dates
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Signed Date <span class="text-red-500">*</span></label>
              <input id="contractSignedDate" type="date" class="swal2-input !m-0 !w-full" value="${customer?.contractSignedDate ? customer.contractSignedDate.split('T')[0] : ''}">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Start Date <span class="text-red-500">*</span></label>
              <input id="contractStartDate" type="date" class="swal2-input !m-0 !w-full" value="${customer?.contractStartDate ? customer.contractStartDate.split('T')[0] : ''}">
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">End Date <span class="text-red-500">*</span></label>
              <input id="contractEndDate" type="date" class="swal2-input !m-0 !w-full" value="${customer?.contractEndDate ? customer.contractEndDate.split('T')[0] : ''}">
            </div>
          </div>
        </div>

        <div class="bg-gray-50 p-4 rounded-lg border border-gray-100">
          <h3 class="text-md font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span class="w-2 h-5 bg-blue-600 rounded-full"></span> System & Address
          </h3>
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Role <span class="text-red-500">*</span></label>
              <select id="role" class="swal2-select !m-0 !w-full">
                ${roles.map(r => `<option value="${r._id}" ${customer?.role?._id === r._id || customer?.role === r._id ? 'selected' : (r._id === DEFAULT_CUSTOMER_ROLE_ID ? 'selected' : '')}>${r.roleName}</option>`).join('')}
              </select>
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Pincode <span class="text-red-500">*</span></label>
              <select id="pincode" class="swal2-select !m-0 !w-full">
                <option value="">Select Pincode</option>
                ${pincodes.map(p => `<option value="${p._id}" ${customer?.pincode?._id === p._id || customer?.pincode === p._id ? 'selected' : ''}>${p.pincode} - ${p.locationname || ''}</option>`).join('')}
              </select>
            </div>
            <div>
              <label class="block font-semibold text-gray-700 mb-1">Branch <span class="text-red-500">*</span></label>
              <select id="branch" class="swal2-select !m-0 !w-full">
                <option value="">Select Branch</option>
                ${branches.map(b => `<option value="${b._id}" ${customer?.branch?._id === b._id || customer?.branch === b._id ? 'selected' : ''}>${b.branchname}</option>`).join('')}
              </select>
            </div>
            <div class="md:col-span-3">
              <label class="block font-semibold text-gray-700 mb-1">Address</label>
              <textarea id="address" class="swal2-textarea !m-0 !w-full" rows="2" placeholder="Full Address">${customer?.address || ''}</textarea>
            </div>
          </div>
        </div>
      </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: isEdit ? 'Update Customer' : 'Create Customer',
      customClass: { container: 'swal2-wide' },
      preConfirm: () => {
        const raw = {
          name: document.getElementById('name').value,
          companyName: document.getElementById('companyName').value,
          email: document.getElementById('email').value,
          mobileNumber: document.getElementById('mobileNumber').value,
          gstNumber: document.getElementById('gstNumber').value,
          panNumber: document.getElementById('panNumber').value,
          contractSignedDate: document.getElementById('contractSignedDate').value,
          contractStartDate: document.getElementById('contractStartDate').value,
          contractEndDate: document.getElementById('contractEndDate').value,
          role: document.getElementById('role').value || DEFAULT_CUSTOMER_ROLE_ID,
          pincode: document.getElementById('pincode').value,
          branch: document.getElementById('branch').value,
          address: document.getElementById('address').value,
        };
        if (!isEdit) {
          raw.password = document.getElementById('password').value;
        } else {
          raw._id = customer._id;
        }

        const errors = validateForm(raw);
        if (Object.keys(errors).length > 0) {
          Swal.showValidationMessage(Object.values(errors).join('<br>'));
          return false;
        }
        return raw;
      },
    });

    if (formData) {
      try {
        if (isEdit) {
          await updateCustomer(customer._id, formData);
          toast.success('Customer updated successfully!');
        } else {
          await createCustomer(formData);
          toast.success('Customer created successfully!');
        }
        fetchCustomers();
      } catch (err) {
        toast.error(err.message || 'Operation failed');
      }
    }
  };

  const handleView = (c) => {
    Swal.fire({
      title: `<strong>${c.companyName || c.name}</strong>`,
      html: `
        <div class="text-left space-y-3 p-2 text-sm">
          <p><strong>Name:</strong> ${c.name || '-'}</p>
          <p><strong>Email:</strong> ${c.email || '-'}</p>
          <p><strong>Mobile:</strong> ${c.mobileNumber || '-'}</p>
          <p><strong>GST:</strong> ${c.gstNumber || '-'}</p>
          <p><strong>PAN:</strong> ${c.panNumber || '-'}</p>
          <p><strong>Address:</strong> ${c.address || '-'}</p>
          <p><strong>Branch:</strong> ${c.branch?.branchname || '-'}</p>
          <p><strong>Pincode:</strong> ${c.pincode?.pincode || '-'}</p>
          <p><strong>Status:</strong> ${c.status || 'Active'}</p>
        </div>
      `,
      confirmButtonText: 'Close',
    });
  };

  const handleDelete = async (id) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
    });

    if (confirm.isConfirmed) {
      try {
        await deleteCustomer(id);
        toast.success('Customer deleted!');
        fetchCustomers();
      } catch (err) {
        toast.error(err.message || 'Failed to delete');
      }
    }
  };

  // Filter & Pagination
  const filtered = customers.filter(c =>
    c.name?.toLowerCase().includes(filter.toLowerCase()) ||
    c.companyName?.toLowerCase().includes(filter.toLowerCase()) ||
    c.email?.toLowerCase().includes(filter.toLowerCase()) ||
    c.mobileNumber?.includes(filter)
  );

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paginatedData = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div>
      <ToastContainer />
      <div className="p-6 max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Customer Master</h1>
            <p className="text-gray-500 text-sm">Manage customers, contracts and locations</p>
          </div>
          <button
            onClick={() => openCustomerModal()}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-lg transition shadow-sm"
          >
            <PlusIcon className="w-5 h-5" /> Add Customer
          </button>
        </div>

        <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
          <div className="relative w-full sm:w-80">
            <IoIosFunnel className="absolute left-3 top-3 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by name, email, or mobile..."
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none"
            />
          </div>
          <button
            onClick={() => toast.info('Export functionality ready')}
            className="flex items-center gap-2 border border-gray-300 hover:bg-gray-50 text-gray-700 px-3 py-2 rounded-lg text-sm transition"
          >
            <FaFileExcel className="text-green-600" /> Export Excel
          </button>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 text-xs uppercase text-gray-500 font-semibold border-b">
                <tr>
                  <th className="py-3.5 px-4">S.No</th>
                  <th className="py-3.5 px-4">Customer Name</th>
                  <th className="py-3.5 px-4">Contact Details</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-gray-400">Loading customers...</td>
                  </tr>
                ) : paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="text-center py-10 text-gray-400">No customers found</td>
                  </tr>
                ) : (
                  paginatedData.map((c, idx) => (
                    <tr key={c._id || idx} className="hover:bg-gray-50/80 transition">
                      <td className="py-3.5 px-4 text-gray-400">{(currentPage - 1) * pageSize + idx + 1}</td>
                      <td className="py-3.5 px-4 font-medium text-gray-800">
                        {c.name}
                        {c.companyName && <span className="block text-xs text-gray-400">{c.companyName}</span>}
                      </td>
                      <td className="py-3.5 px-4">
                        <div>{c.email}</div>
                        <div className="text-xs text-gray-400">{c.mobileNumber}</div>
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          {c.status || 'Active'}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <div className="flex justify-center items-center gap-2">
                          <button onClick={() => handleView(c)} className="p-1.5 hover:bg-gray-100 text-gray-500 hover:text-blue-600 rounded">
                            <FaEye className="w-4 h-4" />
                          </button>
                          <button onClick={() => openCustomerModal(c)} className="p-1.5 hover:bg-gray-100 text-gray-500 hover:text-blue-600 rounded">
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button onClick={() => handleDelete(c._id)} className="p-1.5 hover:bg-gray-100 text-gray-500 hover:text-red-600 rounded">
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          <div className="p-4 border-t flex flex-col sm:flex-row justify-between items-center gap-4 text-sm text-gray-500">
            <div className="flex items-center gap-2">
              <span>Show</span>
              <select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                className="border rounded p-1 outline-none focus:ring-1 focus:ring-blue-500"
              >
                {[5, 10, 20, 50].map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <span>entries</span>
              <span className="ml-4 text-gray-400">
                Showing {Math.min(filtered.length, (currentPage - 1) * pageSize + 1)} to {Math.min(filtered.length, currentPage * pageSize)} of {filtered.length}
              </span>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                className={`p-2 rounded-md border ${currentPage === 1 ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-blue-50 transition'}`}
              >
                <FaArrowLeft className="w-3.5 h-3.5" />
              </button>

              <div className="flex gap-1">
                {[...Array(totalPages)].map((_, idx) => (
                  <button
                    key={idx + 1}
                    onClick={() => setCurrentPage(idx + 1)}
                    className={`px-3 py-1 rounded-md text-sm font-medium transition ${currentPage === idx + 1 ? 'bg-blue-600 text-white shadow-sm' : 'bg-white text-gray-600 hover:bg-gray-100 border'}`}
                  >
                    {idx + 1}
                  </button>
                ))}
              </div>

              <button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
                className={`p-2 rounded-md border ${currentPage === totalPages || totalPages === 0 ? 'bg-gray-100 text-gray-300 cursor-not-allowed' : 'bg-white text-blue-600 hover:bg-blue-50 transition'}`}
              >
                <FaArrowRight className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerTable;